import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';

import {useToast} from '@/components/ui/use-toast';
import {useCreateEvents} from '@/hooks/useEvents';
import {EventSubType, EventType} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

import useEvacuationStore from '../stores/evacuationStore';

type EvacuationSummary = {
  confirmedResidents: number;
  confirmedStaff: number;
  notOnSite: number;
};

export function useEndEvacuation({onEnded}: {onEnded: () => void}) {
  const {t} = useTranslation();
  const {toast} = useToast();
  const createEvents = useCreateEvents();
  const note = useEvacuationStore(s => s.note);
  const reset = useEvacuationStore(s => s.reset);

  const endEvacuation = async ({
    confirmedResidents,
    confirmedStaff,
    notOnSite,
  }: EvacuationSummary) => {
    const trimmedNote = note.trim();
    const content = [
      `${t('Facility evacuation took place')}. ${t(
        'People confirmed in the evacuation area'
      )}: ${confirmedResidents + confirmedStaff} (${t(
        'Residents'
      )}: ${confirmedResidents}, ${t('Staff')}: ${confirmedStaff}).`,
      notOnSite > 0 && `${t('Excluded from headcount')}: ${notOnSite}.`,
      trimmedNote && `${t('Additional notes')}: ${trimmedNote}`,
    ]
      .filter(Boolean)
      .join('\n');

    try {
      await createEvents.mutateAsync([
        {
          content,
          date: dayjs().toISOString(),
          eventSubType: EventSubType.None,
          type: EventType.Casual,
        },
      ]);
    } catch (error) {
      console.error(error);
      toast({
        title: t('Could not save the evacuation event. Please try again.'),
        variant: 'destructive',
      });
      return;
    }

    await queryClient.invalidateQueries({
      predicate: query =>
        ['GetEventsByPairingId', 'GetEventsByDateRange'].includes(
          String(query.queryKey[0])
        ),
    });
    toast({title: t('Evacuation ended'), variant: 'success'});
    reset();
    onEnded();
  };

  return {endEvacuation, isPending: createEvents.isPending};
}
